import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Layout from '../layout/Layout'
import InvoiceForm from '../molecules/InvoiceForm'
import ChevronRightSvg from '../other/svg/ChevronRightSvg'
import { InvoiceFormData } from 'src/helpers/invoiceFormValidation'
import { FETCH_INITIAL_INVOICE_VALUES, RESET_INVOICE_PDF, UPDATE_INVOICE } from 'src/redux/invoiceReducer'
import { RootReducer } from 'src/redux/rootReducer'
export interface InitialFormValues {
  vat_option: string
  net_price: number
  invoice_number: string
  invoice_date: string
  delivery_date: string
  down_payment: number
  price: number
  price_type: string
  vin: string
  motorbike_id: string | number
  motorbike_name: string
  motorbike_year?: string
  motorbike_mileage?: number | null
  language: string
  additional_text: string
  currency_code?: string
  payment_method?: string | null
  payment_due_date?: string
  client_id?: string | number
  client_name?: string
  client_tax_number?: string
  client_street?: string
  client_house_number?: string
  client_zip_code?: string
  client_place?: string
}

interface UrlParams {
  clientId: string
  invoiceId: string
  [key: string]: string | undefined
}

const InvoiceUpdate = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { clientId, invoiceId } = useParams<UrlParams>()
  const loading = useSelector((state: RootReducer) => state.invoiceReducer.loading)
  const invoicePdf = useSelector((state: RootReducer) => state.invoiceReducer.invoice_pdf)

  useEffect(() => {
    return () => {
      dispatch(RESET_INVOICE_PDF())
    }
  }, [dispatch])

  useEffect(() => {
    dispatch(FETCH_INITIAL_INVOICE_VALUES({ id: invoiceId ?? '' }))
  }, [dispatch, invoiceId])

  useEffect(() => {
    if (invoicePdf !== null) {
      window.open(invoicePdf, '_blank')
      navigate(`/selling/clients/${clientId ?? ''}`)
    }
  }, [invoicePdf, navigate, clientId])

  const handleSubmit = (values: InvoiceFormData): void => {
    dispatch(UPDATE_INVOICE({ values, id: invoiceId ?? '' }))
  }

  const handleCancel = (): void => {
    navigate(`/selling/clients/${clientId ?? ''}`)
  }

  const initialFormValues = useSelector((state: RootReducer) => {
    return state.invoiceReducer.initialInvoiceValues[invoiceId ?? ''] ?? null
  })

  return (
    <Layout withoutToolbar>
      <div className="flex flex-col justify-start items-start gap-4 h-[calc(100vh-100px)]">
        <div className="self-stretch min-h-14 bg-Estigroup-primary rounded-lg border border-neutral-200 flex flex-col justify-start items-center">
          <div className="self-stretch px-4 py-3 inline-flex justify-start items-center gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="text-foregrounds-fg-on-color text-xl font-semibold font-Montserrat leading-5">
              {t('invoice_form.sales_invoices')}
            </button>
            <ChevronRightSvg className="size-3.5 text-foregrounds-fg-on-color" />
            <div className="text-foregrounds-fg-on-color text-xl font-semibold font-Montserrat leading-5">
              {t('invoice_form.edit_invoice')}
            </div>
          </div>
        </div>
        {initialFormValues !== null && (
          <InvoiceForm
            initialValues={{
              ...initialFormValues,
              price: String(initialFormValues.price ?? initialFormValues.net_price ?? ''),
              price_type: initialFormValues.price_type === 'gross' ? 'gross' : 'net',
              currency_code: initialFormValues.currency_code ?? 'PLN',
              payment_method: initialFormValues.payment_method ?? '',
              payment_due_date: initialFormValues.payment_due_date ?? '',
              additional_text: initialFormValues.additional_text ?? ''
            }}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            mode="edit"
            loading={loading}
          />
        )}
      </div>
    </Layout>
  )
}

export default InvoiceUpdate
